const post = {
  "slug": "understand-checkout-dm-analytics-numbers",
  "title": "What Every Number in Checkout DM Analytics Means",
  "seoTitle": "Checkout DM Analytics: What Each Number Means",
  "description": "A plain guide to the Checkout DM Analytics page: revenue, conversion, trends, forecast and customer segments, and what each number does and does not measure.",
  "excerpt": "Some Analytics numbers mean something narrower than they sound. Here is exactly what each one counts, which plan unlocks it and why it may not update instantly.",
  "category": "Product",
  "tags": [
    "Checkout DM Analytics",
    "Instagram sales analytics",
    "Customer segments",
    "Conversion rate"
  ],
  "date": "2026-11-12",
  "cover": {
    "src": "/blog-images/understand-checkout-dm-analytics-numbers-cover.png",
    "alt": "A dashboard card showing revenue, top products and customer segments such as VIP and At Risk"
  },
  "blocks": [
    {
      "type": "p",
      "text": "An analytics page is only useful if you know what each number counts. A few figures in Checkout DM Analytics mean something narrower than they sound, and reading them the wrong way can send you chasing a problem that is not there. This guide walks through the page from top to bottom."
    },
    {
      "type": "h2",
      "text": "Which plan shows what"
    },
    {
      "type": "p",
      "text": "Growth includes revenue and conversion analytics. Pro adds deeper, more advanced reporting. In practice, the top row of numbers and the two big charts under it are available on Growth and Pro, while the funnel, operations figures, forecast and customer segments are Pro only. A range picker lets you switch between 7, 30 and 90 days, and most numbers follow it."
    },
    {
      "type": "h2",
      "text": "The top row of numbers"
    },
    {
      "type": "ul",
      "items": [
        "**Revenue:** the sum of paid orders in the selected range. An order counts if its status is Paid or Paid, Manual Review.",
        "**Chats:** conversations with any activity in the range, not only ones that started in it. A customer who first messaged weeks ago and comes back to order today still counts, because the conversation is still active.",
        "**Paid orders:** the number of orders in the range with a Paid or Paid, Manual Review status.",
        "**Average order value:** revenue divided by paid orders for the same range.",
        "**Conversion:** paid orders divided by chats. If there were no active chats in the range, it shows a dash instead of 0%, because a true 0% would wrongly suggest that nothing converted.",
        "**Returns filed:** return requests filed in the range. They are not necessarily for the same orders as the paid orders beside them, because a return this week can belong to an order placed weeks ago. That is why the percentage of paid orders can legitimately go above 100%."
      ]
    },
    {
      "type": "callout",
      "tone": "lilac",
      "title": "How to read the trend arrows",
      "text": "The arrows compare the second half of your selected range with the first half of the same range. They are not a comparison with the previous period. Read them as a sign of whether things are speeding up or slowing down inside this window, not as a comparison with last month."
    },
    {
      "type": "h2",
      "text": "Charts and trends"
    },
    {
      "type": "ul",
      "items": [
        "**Revenue over time:** daily revenue for the range, paid orders only. Hover a point for the exact date and amount.",
        "**Best day:** the single highest-revenue day in the range, marked on the chart.",
        "**DM engagement:** the same definition of Chats as the top row, shown day by day.",
        "**Orders per day:** paid orders, day by day.",
        "**Revenue by day of week:** total revenue for each weekday across the whole range, so you can see which days actually convert."
      ]
    },
    {
      "type": "h2",
      "text": "Which products are working"
    },
    {
      "type": "p",
      "text": "Top products shows your best sellers by revenue, paid orders only, up to the top 8 in the range. Dead stock shows catalog items with the fewest units sold, including zero. Checkout DM does not track product views, so dead stock is an honest stand-in for the lowest-converting product. It tells you what is not selling, not why. If a product sits there, the usual first step is to look at its photo and description. Our guide to [writing product descriptions for Instagram sellers](/blog/write-product-descriptions-instagram-sellers) is a good place to start."
    },
    {
      "type": "h2",
      "text": "Automation performance"
    },
    {
      "type": "p",
      "text": "Auto-resolved conversations is a real count of conversations that reached a completed state, meaning a payment confirmed or a return handled, without ever landing in manual review. Estimated hours and staff cost saved is different. It is a calculator, not a measurement: it multiplies the real auto-resolved count by two numbers you set yourself, minutes per manual reply and hourly staff cost. Those assumptions are saved in your browser only, so change them to match your own team."
    },
    {
      "type": "h2",
      "text": "Pro: funnel and operations"
    },
    {
      "type": "ul",
      "items": [
        "**DM to Paid funnel:** three stages. DMs received (the same as Chats), Checkout started (an order record was created, which is the closest available stand-in for reaching checkout) and Paid.",
        "**Order status breakdown:** every order created in the range, grouped by its current status.",
        "**Average DM to checkout time:** the average gap between a customer\u2019s first message and an order being created for them.",
        "**Average return resolution time:** the average gap between a return request and your approval or rejection.",
        "**Cart abandonment:** the percentage of conversations in the range with items in the cart that never reached a finished state. For ways to recover these, see our guide to [abandoned carts in Instagram DMs](/blog/abandoned-carts-instagram-dms).",
        "**When DMs come in:** the hour of the day, 0 to 23, when chats most often start. It is useful for knowing when to be online."
      ]
    },
    {
      "type": "h2",
      "text": "Pro: forecast and customer segments"
    },
    {
      "type": "p",
      "text": "The revenue forecast is a simple run rate for the current calendar month: month-to-date revenue divided by days elapsed, multiplied by the days in the month. It is today\u2019s pace held steady, not a predictive model. Unlike most of the page, it always uses the current month, whatever range you have picked."
    },
    {
      "type": "p",
      "text": "Customer segments are based on a customer\u2019s entire order history, not just the selected range. That is deliberate: calling someone Lost on the strength of a 7-day window would make no sense. The rules are:"
    },
    {
      "type": "table",
      "headers": [
        "Segment",
        "Rule"
      ],
      "rows": [
        [
          "VIP",
          "3 or more orders, and total spend above the average paying customer"
        ],
        [
          "Loyal",
          "2 or more orders, the most recent within the last 60 days"
        ],
        [
          "At Risk",
          "2 or more orders, but nothing in the last 60 days or more"
        ],
        [
          "Lost",
          "Nothing ordered in 120 days or more"
        ],
        [
          "New",
          "Exactly one order, placed in the last 30 days"
        ],
        [
          "Regular",
          "Everyone else: a single order, more than 30 days ago"
        ]
      ]
    },
    {
      "type": "p",
      "text": "A customer gets exactly one badge, in this order of priority: Lost, At Risk, VIP, Loyal, New, Regular. A big spender who has gone quiet therefore shows as Lost or At Risk rather than VIP, on purpose, because a valuable customer going quiet is the more useful thing to see. Top spenders is separate again: your 5 highest all-time spenders by total paid order value, ignoring order count and recency, so someone with one large order can appear there even if their badge is New, Lost or Regular."
    },
    {
      "type": "h2",
      "text": "Why numbers do not always update instantly"
    },
    {
      "type": "p",
      "text": "Analytics is cached in your browser for up to 2 hours per range, so reopening the page or switching to a range you have already viewed will not re-query anything until that cache expires. Press Refresh at any time to pull fresh numbers. Customer segments and the repeat-customer rate refresh on their own 24-hour schedule, because they are computed from your whole order history."
    },
    {
      "type": "callout",
      "tone": "mint",
      "title": "A quick sanity check",
      "text": "If a number looks odd, check its definition before acting on it. A dash in Conversion means no active chats, not zero sales. A Returns filed figure above 100% of paid orders can be normal. A Lost badge on a big spender is the page working as designed."
    }
  ],
  "related": [
    {
      "to": "/analytics-guide",
      "title": "Analytics Guide",
      "description": "The full definition of every metric on the Analytics page."
    },
    {
      "to": "/status-guide",
      "title": "Status Badges Guide",
      "description": "What each badge means in Chats, Orders and Returns."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "See which plan includes the analytics you need."
    }
  ],
  "faqs": [
    {
      "q": "Which plans include Analytics?",
      "a": "Growth includes revenue and conversion analytics, and Pro adds deeper reporting such as the funnel, forecast and customer segments."
    },
    {
      "q": "Why does Conversion show a dash instead of 0%?",
      "a": "A dash appears when there were no active chats in the selected range. A true 0% would wrongly suggest that chats happened and none converted."
    },
    {
      "q": "Is the revenue forecast a prediction?",
      "a": "No. It is a run rate: month-to-date revenue divided by days elapsed, multiplied by the number of days in the month. It holds today\u2019s pace steady and always uses the current month."
    },
    {
      "q": "Why is a big spender badged Lost or At Risk instead of VIP?",
      "a": "Each customer gets one badge, and Lost and At Risk take priority over VIP. A valuable customer who has gone quiet is the more actionable thing to notice."
    },
    {
      "q": "Why have my numbers not changed since I last looked?",
      "a": "Analytics is cached in your browser for up to 2 hours per range. Press Refresh to force a fresh pull. Customer segments refresh on a separate 24-hour schedule."
    }
  ]
};

export default post;
